<template>
    <transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4"
    >
        <div
            v-if="visible"
            class="fixed bottom-6 right-6 z-50 w-80 rounded-xl bg-white shadow-xl ring-1 ring-black/10"
            data-testid="getting-started-checklist"
        >
            <!-- Header -->
            <div
                class="flex items-center justify-between rounded-t-xl bg-sky-500 px-4 py-3"
            >
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="cursor-pointer text-white/80 hover:text-white"
                        :aria-label="
                            collapsed
                                ? 'Expand checklist'
                                : 'Collapse checklist'
                        "
                        @click="collapsed = !collapsed"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="h-4 w-4 transition-transform"
                            :class="collapsed ? 'rotate-180' : ''"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                            />
                        </svg>
                    </button>
                    <span class="text-sm font-medium text-white"
                        >Getting Started</span
                    >
                </div>
            </div>

            <!-- Progress bar -->
            <div v-if="!collapsed" class="px-4 pt-3 pb-1">
                <div class="mb-1 flex justify-between text-xs text-taupe-500">
                    <span
                        >{{ store.completedCount }} of
                        {{ store.total }} complete</span
                    >
                    <span>{{ store.percentComplete }}%</span>
                </div>
                <div
                    class="h-1.5 w-full overflow-hidden rounded-full bg-taupe-100"
                >
                    <div
                        class="h-full rounded-full bg-sky-500 transition-all duration-500"
                        :style="{ width: store.percentComplete + '%' }"
                        data-testid="checklist-progress-bar"
                    ></div>
                </div>
            </div>

            <!-- All-done state -->
            <div
                v-if="!collapsed && allDone"
                class="px-4 py-4 text-center"
                data-testid="checklist-all-done"
            >
                <p class="text-2xl">🎉</p>
                <p class="mt-1 text-sm font-medium text-taupe-800">
                    You're all set!
                </p>
                <p class="mt-0.5 text-xs text-taupe-500">
                    The checklist will close shortly.
                </p>
            </div>

            <!-- Steps -->
            <ul
                v-else-if="!collapsed"
                class="divide-y divide-taupe-100 px-4 py-2"
                data-testid="checklist-steps"
            >
                <li
                    v-for="step in resolvedSteps"
                    :key="step.key"
                    class="flex items-start gap-3 py-2.5"
                    :data-testid="`checklist-step-${step.key}`"
                >
                    <!-- Check icon -->
                    <button
                        type="button"
                        class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                        :class="
                            step.completed
                                ? 'border-sky-500 bg-sky-500 cursor-default'
                                : 'border-taupe-300 bg-white hover:border-sky-400 hover:bg-sky-50 cursor-pointer'
                        "
                        :disabled="step.completed"
                        :aria-label="
                            step.completed
                                ? null
                                : `Mark ${step.label} as complete`
                        "
                        @click="!step.completed && store.markComplete(step.key)"
                    >
                        <svg
                            v-if="step.completed"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            class="h-3 w-3 text-white"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M4.5 12.75l6 6 9-13.5"
                            />
                        </svg>
                    </button>

                    <div class="flex min-w-0 flex-1 flex-col">
                        <span
                            class="text-sm leading-tight"
                            :class="
                                step.completed
                                    ? 'text-taupe-400 line-through'
                                    : 'font-medium text-taupe-800'
                            "
                        >
                            {{ step.label }}
                        </span>
                        <button
                            v-if="!step.completed && step.action"
                            type="button"
                            class="cursor-pointer mt-1 w-fit text-xs font-medium text-sky-600 hover:text-sky-800"
                            @click="step.action"
                        >
                            {{ step.actionLabel }} →
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useOnboardingStore } from '@/stores/onboarding'
import { useSessionStore } from '@/stores/session'

const emit = defineEmits(['open-sdk-wizard'])

const store = useOnboardingStore()
const sessionStore = useSessionStore()
const router = useRouter()

const accountUlid = computed(
    () => sessionStore.data?.account?.data?.ulid ?? null
)

function seenKey(ulid) {
    return `onboarding-seen-${ulid}`
}

// Default expanded; collapse once we know the account and they've seen it before
const collapsed = ref(false)

watch(
    accountUlid,
    (ulid) => {
        if (ulid) {
            collapsed.value = localStorage.getItem(seenKey(ulid)) === 'true'
        }
    },
    { immediate: true }
)

watch(collapsed, (isCollapsed) => {
    if (!isCollapsed && accountUlid.value) {
        localStorage.setItem(seenKey(accountUlid.value), 'true')
    }
})
const allDone = computed(() => store.total > 0 && store.percentComplete === 100)

const current = computed(() => ({
    projectKey: sessionStore.data?.current?.projectKey,
    environmentKey: sessionStore.data?.current?.environmentKey,
}))

const STEP_CONFIG = {
    account_created: {
        label: 'Create your account',
        actionLabel: null,
        action: null,
    },
    sdk_connected: {
        label: 'Connect your first SDK',
        actionLabel: 'Open SDK wizard',
        action: () => {
            emit('open-sdk-wizard')
        },
    },
    flag_created: {
        label: 'Create your first flag',
        actionLabel: 'Go to Flags',
        action: () =>
            router.push({ name: 'ProjectFlagsScreen', params: current.value }),
    },
    teammate_invited: {
        label: 'Invite a teammate',
        actionLabel: 'Invite users',
        action: () => router.push({ name: 'AdminUsersScreen' }),
    },
    billing_added: {
        label: 'Add billing / start your plan',
        actionLabel: 'Go to Billing',
        action: () => router.push({ name: 'AdminBillingScreen' }),
    },
}

const resolvedSteps = computed(() =>
    store.checklistSteps.map((step) => {
        const config = STEP_CONFIG[step.key] ?? {
            label: step.label ?? step.key,
            actionLabel: 'Go',
            action: null,
        }
        return {
            ...step,
            label: config.label,
            actionLabel: config.actionLabel,
            action: config.action,
        }
    })
)

const visible = computed(() => store.total > 0 && !store.dismissed)

function handleOpenEvent() {
    collapsed.value = false
}

function addOpenListener() {
    window.addEventListener('zenmanage:open-getting-started', handleOpenEvent)
}

function removeOpenListener() {
    window.removeEventListener(
        'zenmanage:open-getting-started',
        handleOpenEvent
    )
}

onMounted(addOpenListener)
onUnmounted(removeOpenListener)

watch(allDone, (done) => {
    if (done) {
        // brief celebration then hide
        setTimeout(() => {
            store.dismiss()
        }, 3000)
    }
})
</script>
